import { PropsWithChildren, ReactNode } from "react";
import Button from "../Button/Button";
import { AromaSectionHeaderVariantProps } from "@zenchef/styled-system/recipes";
import { token } from "@zenchef/styled-system/tokens";
import { useWindowSize } from "../../utils";
import Icon from "../Icon/Icon";
import { IconName } from "@zenchef/icomoon/dist/aromaticons/types";
import withTestId from "../../utils/withTestId";

import {
  SectionHeaderContainer,
  SectionHeaderInnerWrap,
  SectionHeaderWrap,
  SectionHeaderLayout,
  SectionHeaderTitle,
} from "./section-header-parts";

interface ButtonProps {
  label: string;
  iconName: IconName;
  showIcon?: boolean;
  iconOnly?: boolean;
  onClick: () => void;
}

export interface SectionHeaderProps
  extends AromaSectionHeaderVariantProps,
    PropsWithChildren {
  title: string;
  tagComponent?: ReactNode;
  mainButton?: ButtonProps;
  secondaryButton?: ButtonProps;
  className?: string;
}

const getButtonContent = (
  button: ButtonProps,
  isIconOnly: boolean,
): ReactNode => {
  if (isIconOnly) {
    return <Icon name={button.iconName} />;
  }

  if (button.showIcon) {
    return (
      <>
        <Icon name={button.iconName} />
        {button.label}
      </>
    );
  }

  return button.label;
};

const AromaSectionHeader = ({
  title,
  tagComponent,
  mainButton,
  secondaryButton,
  children,
  ...props
}: SectionHeaderProps) => {
  const { width: windowWidth } = useWindowSize();
  const isMobileScreen = windowWidth <= parseInt(token("breakpoints.md"), 10);
  const isMainIconOnly = mainButton?.iconOnly ?? isMobileScreen;
  const isSecondaryIconOnly = secondaryButton?.iconOnly ?? isMobileScreen;

  return (
    <SectionHeaderLayout
      {...props}
      actions={
        <>
          {secondaryButton && (
            <Button
              hierarchy="secondary-subtle"
              iconOnly={isSecondaryIconOnly}
              onClick={secondaryButton.onClick}
            >
              {getButtonContent(secondaryButton, isSecondaryIconOnly)}
            </Button>
          )}
          {mainButton && (
            <Button iconOnly={isMainIconOnly} onClick={mainButton.onClick}>
              {getButtonContent(mainButton, isMainIconOnly)}
            </Button>
          )}
          {children}
        </>
      }
    >
      <SectionHeaderTitle>{title}</SectionHeaderTitle>
      {tagComponent}
    </SectionHeaderLayout>
  );
};

export default Object.assign(
  withTestId("header")(AromaSectionHeader),
  // Export sub components
  {
    Container: SectionHeaderContainer,
    Wrap: SectionHeaderWrap,
    InnerWrap: SectionHeaderInnerWrap,
    Layout: SectionHeaderLayout,
    Title: SectionHeaderTitle,
  },
);
